import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Icon from "@mui/material/Icon";

import {
  SimulatableKind,
  useSimulateDetection,
} from "../api/realtimeObservations";

interface SimulateDetectionDialogProps {
  open: boolean;
  onClose: () => void;
  /** Where the user clicked on the map. */
  latlng: { lat: number; lng: number } | null;
  /** Called after a successful create so the caller can make sure the kind's
   *  layer is visible. */
  onCreated?: (kind: SimulatableKind) => void;
}

const LIFESPAN_PRESETS: { label: string; minutes: number }[] = [
  { label: "15 min", minutes: 15 },
  { label: "1 h", minutes: 60 },
  { label: "6 h", minutes: 360 },
  { label: "24 h", minutes: 1440 },
];

const MIN_LIFESPAN_MINUTES = 1;
const MAX_LIFESPAN_MINUTES = 7 * 24 * 60;

/**
 * Creates a simulated hotspot or lightning detection at a clicked map point,
 * with a user-specified lifespan. The detection is stored server-side, shows
 * on the realtime layers for everyone in the tenant, exercises the realtime
 * notification pipeline like a real detection, and disappears when the
 * lifespan elapses.
 */
const SimulateDetectionDialog: React.FC<SimulateDetectionDialogProps> = ({
  open,
  onClose,
  latlng,
  onCreated,
}) => {
  const simulate = useSimulateDetection();

  const [kind, setKind] = useState<SimulatableKind>("hotspot");
  const [lifespanMinutes, setLifespanMinutes] = useState<number>(60);
  const [customLifespan, setCustomLifespan] = useState<string>("");
  const [frp, setFrp] = useState<string>("25");
  const [amplitude, setAmplitude] = useState<string>("20");
  const [polarity, setPolarity] = useState<"-" | "+">("-");
  const [error, setError] = useState<string | null>(null);

  // Fresh state per placement; keep the kind, which tends to repeat.
  useEffect(() => {
    if (open) {
      setError(null);
      simulate.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const usingCustom = customLifespan !== "";
  const customMinutes = Number(customLifespan);
  const effectiveLifespan = usingCustom ? customMinutes : lifespanMinutes;
  const lifespanValid =
    Number.isFinite(effectiveLifespan) &&
    effectiveLifespan >= MIN_LIFESPAN_MINUTES &&
    effectiveLifespan <= MAX_LIFESPAN_MINUTES;

  const handleCreate = async () => {
    if (!latlng || !lifespanValid) return;
    setError(null);
    const properties =
      kind === "hotspot"
        ? { frp: Number(frp) || undefined }
        : {
            amplitude_ka: Number(amplitude) || undefined,
            polarity,
          };
    try {
      await simulate.mutateAsync({
        kind,
        lat: latlng.lat,
        lon: latlng.lng,
        lifespan_minutes: Math.round(effectiveLifespan),
        properties,
      });
      onCreated?.(kind);
      onClose();
    } catch (err) {
      setError(
        (err as { message?: string })?.message ||
          "Failed to create the simulated detection"
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Simulate a detection</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Places a simulated detection at{" "}
          {latlng
            ? `${latlng.lat.toFixed(4)}, ${latlng.lng.toFixed(4)}`
            : "the clicked point"}
          . It appears on the realtime layers for your whole organisation and
          triggers detection notifications like a real one, then disappears
          after the lifespan you set.
        </Typography>

        <ToggleButtonGroup
          value={kind}
          exclusive
          fullWidth
          size="small"
          onChange={(_, value) => value && setKind(value)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="hotspot">
            <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>local_fire_department</Icon>
            Hotspot
          </ToggleButton>
          <ToggleButton value="lightning">
            <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>bolt</Icon>
            Lightning
          </ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Lifespan
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mt: 0.5, mb: 1 }}>
          {LIFESPAN_PRESETS.map((preset) => (
            <Button
              key={preset.minutes}
              size="small"
              variant={
                !usingCustom && lifespanMinutes === preset.minutes
                  ? "contained"
                  : "outlined"
              }
              onClick={() => {
                setLifespanMinutes(preset.minutes);
                setCustomLifespan("");
              }}
              sx={{ flex: 1, minWidth: 0, textTransform: "none" }}
            >
              {preset.label}
            </Button>
          ))}
        </Box>
        <TextField
          size="small"
          fullWidth
          label="Custom lifespan"
          type="number"
          value={customLifespan}
          onChange={(e) => setCustomLifespan(e.target.value)}
          error={usingCustom && !lifespanValid}
          helperText={
            usingCustom && !lifespanValid
              ? `Between ${MIN_LIFESPAN_MINUTES} and ${MAX_LIFESPAN_MINUTES} minutes (7 days)`
              : undefined
          }
          InputProps={{
            endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
          }}
          sx={{ mb: 2 }}
        />

        {kind === "hotspot" ? (
          <TextField
            size="small"
            fullWidth
            label="Fire power (FRP)"
            type="number"
            value={frp}
            onChange={(e) => setFrp(e.target.value)}
            InputProps={{
              endAdornment: <InputAdornment position="end">MW</InputAdornment>,
            }}
            helperText="Drives the hotspot's size on the map"
          />
        ) : (
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              size="small"
              select
              label="Polarity"
              value={polarity}
              onChange={(e) => setPolarity(e.target.value as "-" | "+")}
              sx={{ width: 140 }}
            >
              <MenuItem value="-">Negative (−)</MenuItem>
              <MenuItem value="+">Positive (+)</MenuItem>
            </TextField>
            <TextField
              size="small"
              fullWidth
              label="Amplitude"
              type="number"
              value={amplitude}
              onChange={(e) => setAmplitude(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">kA</InputAdornment>,
              }}
            />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!latlng || !lifespanValid || simulate.isPending}
          startIcon={
            simulate.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          Place detection
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SimulateDetectionDialog;
